import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollProgress from "../components/ScrollProgress";
import BackToTop from "../components/BackToTop";
import { useLenis } from "../hooks/useLenis";
import { useReveal } from "../hooks/useReveal";
import { useLang } from "../contexts/LanguageContext";
import { Calendar, Tag, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderContent = (content) => {
  if (!content) return null;
  // If content already contains tags, render as HTML; otherwise split paragraphs by blank line
  if (/<\/?[a-z][\s\S]*>/i.test(content)) {
    return (
      <div
        className="prose-blog"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  return (
    <div className="prose-blog space-y-5">
      {content.split(/\n{2,}/).map((p, i) => (
        <p key={i} className="text-[#1A201C] leading-relaxed">
          {p}
        </p>
      ))}
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const ref = useReveal();
  useLenis();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        setPost(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const backLabel = { en: "← Back to blog", fr: "← Retour au blog", ua: "← Назад до блогу" }[lang];

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(lang === "ua" ? "uk-UA" : lang, {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-sm text-[#4E5750]">Loading…</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 pt-32">
          <h1 className="text-3xl font-bold mb-3">Article not found</h1>
          <button
            onClick={() => navigate("/blog")}
            className="mt-4 px-6 py-3 bg-[#1A201C] text-white rounded-full text-sm tracking-wider uppercase"
          >
            Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div ref={ref} data-testid="blog-post-page" className="relative bg-[#FDFBF7]">
      <ScrollProgress />
      <Header />

      <section className="relative pt-32 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#4E5750] hover:text-[#DE9E36] mb-8"
          >
            <ArrowLeft size={14} /> {backLabel}
          </Link>

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] tracking-[0.22em] uppercase bg-[#F3EFE7] text-[#4E5750]"
                >
                  <Tag size={10} /> {t}
                </span>
              ))}
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#1A201C] leading-[1.05] tracking-tight">
            {post.title}
          </h1>

          <div className="mt-6 flex items-center gap-4 text-sm text-[#4E5750]">
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {formatDate(post.published_at || post.created_at)}
            </span>
            <span>·</span>
            <span>{post.author || "EKOBIOTEK"}</span>
          </div>
        </div>
      </section>

      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mb-12">
          <div className="aspect-[16/9] overflow-hidden border border-[#E5E0D8]">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      <article className="pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {post.excerpt && (
            <p className="text-lg sm:text-xl text-[#4E5750] leading-relaxed mb-10 border-l-4 border-[#DE9E36] pl-6">
              {post.excerpt}
            </p>
          )}
          {renderContent(post.content)}
        </div>
      </article>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default BlogPost;
